import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

/**
 * Encapsulates Web Speech API setup for the Lumera Companion:
 *  - Voice input (SpeechRecognition)
 *  - Voice output (SpeechSynthesis)
 *  - Microphone permission handling (critical on mobile Safari/Chrome)
 *
 * The caller provides `onTranscript` which receives raw recognized speech.
 */
export default function useLumeraVoice({ language, onTranscript }) {
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [micPermission, setMicPermission] = useState('prompt');
  const recognitionRef = useRef(null);
  const synthRef = useRef(typeof window !== 'undefined' ? window.speechSynthesis : null);
  const isMobile = typeof navigator !== 'undefined'
    && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const es = language === 'es';

  // One-time: create SpeechRecognition instance.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error(es ? 'Reconocimiento de voz no disponible en este navegador' : 'Voice recognition not available in this browser');
      return;
    }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = es ? 'es-ES' : 'en-US';
    rec.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setIsListening(false);
      onTranscript?.(transcript);
    };
    rec.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed' || event.error === 'permission-denied') {
        setMicPermission('denied');
        toast.error(es
          ? 'Permiso de micrófono denegado. Habilítalo en la configuración del navegador.'
          : 'Microphone permission denied. Enable it in browser settings.');
      } else {
        toast.error(es ? 'Error de reconocimiento de voz' : 'Voice recognition error');
      }
    };
    rec.onend = () => setIsListening(false);
    recognitionRef.current = rec;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update language on the fly.
  useEffect(() => {
    if (recognitionRef.current) recognitionRef.current.lang = es ? 'es-ES' : 'en-US';
  }, [es]);

  const requestMicPermission = async () => {
    try {
      if (navigator.permissions?.query) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        setMicPermission(result.state);
        result.onchange = () => setMicPermission(result.state);
      }
      if (isMobile) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        setMicPermission('granted');
      }
      return true;
    } catch (err) {
      console.error('Microphone permission error:', err);
      setMicPermission('denied');
      toast.error(es
        ? 'Permiso de micrófono requerido. Por favor, habilítalo en la configuración.'
        : 'Microphone permission required. Please enable it in settings.');
      return false;
    }
  };

  const toggleListening = async () => {
    const rec = recognitionRef.current;
    if (isListening) {
      rec?.stop();
      setIsListening(false);
      return;
    }
    if (micPermission === 'denied') {
      toast.error(es
        ? 'Permiso de micrófono denegado. Habilítalo en la configuración del navegador.'
        : 'Microphone permission denied. Enable it in browser settings.');
      return;
    }
    if (micPermission !== 'granted' && isMobile) {
      const ok = await requestMicPermission();
      if (!ok) return;
    }
    if (!rec) {
      toast.error(es ? 'Reconocimiento de voz no disponible' : 'Voice recognition not available');
      return;
    }
    try {
      rec.lang = es ? 'es-ES' : 'en-US';
      rec.start();
      setIsListening(true);
      toast.info(
        isMobile
          ? (es ? '🎤 Escuchando... Habla ahora' : '🎤 Listening... Speak now')
          : (es ? 'Escuchando...' : 'Listening...'),
        isMobile ? { duration: 3000 } : undefined,
      );
    } catch (err) {
      console.error('Error starting recognition:', err);
      setIsListening(false);
      toast.error(es ? 'Error al iniciar reconocimiento de voz' : 'Error starting voice recognition');
    }
  };

  const speak = (text) => {
    if (!synthRef.current || !text) return;
    synthRef.current.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = es ? 'es-ES' : 'en-US';
    u.rate = 0.9;
    u.pitch = 1.0;
    u.onstart = () => setIsSpeaking(true);
    u.onend = () => setIsSpeaking(false);
    u.onerror = () => setIsSpeaking(false);
    synthRef.current.speak(u);
  };

  const stopSpeaking = () => {
    if (!synthRef.current) return;
    synthRef.current.cancel();
    setIsSpeaking(false);
  };

  return {
    isListening,
    isSpeaking,
    micPermission,
    isMobile,
    toggleListening,
    speak,
    stopSpeaking,
  };
}
